import Navigator from "../../component/navigator";
import styles from "./index.module.scss";
import Button from "./components/button";
import Table from "rc-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChallengeBase,
  ChallengeList,
  ChallengeProgress,
  ChallengeStatus,
  ChallengeTag,
  Difficulty,
} from "../../types/Challenge";
import {
  GetAllChallengeParams,
  getAllChallenges,
  getChallengeDynamicTags,
  getChallengeProgress,
  getChallengeWechatGroupQrcode,
} from "../../api";
import CorrectFilled from "../../icons/correct-filled";
import ErrorFilled from "../../icons/error-filled";
import { AlignType } from "rc-table/es/interface";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import classes from "classnames";
import { Circle } from "rc-progress";
import DifficultyStats from "./components/difficulty-stats";
import CardFilter from "./components/card-filter";
import DynamicTagsFilter from "./components/dynamic-tags-filter";
import { Loading, Modal } from "@quark/design";
import { Link } from "react-router-dom";
import RankList from "./components/rank-list";
import ChallengeCalendar from "./components/challenge-calendar";
import useDocumentTitle from "../../hooks/use-document-title";
import { useKeyPress } from "ahooks";

const useChallenges = () => {
  const { catalog = "all", page } = useParams<{
    page: string;
    catalog: string;
  }>();
  const navigate = useNavigate();
  const prevParams = useRef<Omit<GetAllChallengeParams, "page" | "catalog">>(
    {}
  );

  const [challengeList, setChallengeList] = useState<ChallengeList>({
    count: 0,
    results: [],
  });

  const [progress, setProgress] = useState<ChallengeProgress>({
    problem_stat: {
      All: 1,
      Easy: 1,
      Medium: 1,
      Hard: 1,
    },
    finish_stat: {
      All: 0,
      Easy: 0,
      Medium: 0,
      Hard: 0,
    },
  });
  const [tags, setTags] = useState<ChallengeTag[]>([]);
  const [fetching, setFetching] = useState(true);

  const updateChallengeList = useCallback(
    ({ ...params }: GetAllChallengeParams) => {
      setFetching(true);
      getAllChallenges({ page: 1, catalog, ...params })
        .then(setChallengeList)
        .then(() => {
          setFetching(false);
          navigate("../1", {
            relative: "path",
          });
          prevParams.current = params;
        })
        .then(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
    },
    [catalog]
  );

  useEffect(() => {
    getChallengeProgress(catalog).then(setProgress);
  }, [catalog]);

  useEffect(() => {
    getChallengeDynamicTags(catalog).then(setTags);
  }, [catalog]);

  useEffect(() => {
    setFetching(true);
    getAllChallenges({
      page: Number(page),
      catalog,
      ...prevParams.current,
    })
      .then(setChallengeList)
      .finally(() => {
        setFetching(false);
      });
  }, [page, catalog]);

  return {
    challengeList,
    updateChallengeList,
    fetching,
    page,
    progress,
    tags,
    catalog,
  };
};

const useWechatGroupQrcode = () => {
  const [qrcodeSrc, setQrcodeSrc] = useState("");
  useEffect(() => {
    getChallengeWechatGroupQrcode().then((data) => {
      setQrcodeSrc(data);
    });
  }, []);
  return qrcodeSrc;
};

const Challenges = () => {
  const {
    challengeList,
    updateChallengeList,
    fetching,
    page,
    progress,
    tags,
    catalog,
  } = useChallenges();
  const location = useLocation();
  const wechatGroupQrcode = useWechatGroupQrcode();
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>(
    []
  );
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [selectedTopTag, setSelectedTopTag] = useState<ChallengeTag | null>(
    null
  );
  useDocumentTitle("题库");

  const [showQrcode, setShowQrcode] = useState(false);

  const sideTags = useMemo(() => {
    if (!selectedTopTag) {
      return tags;
    }
    return tags.filter((t) => t.id === selectedTopTag.id);
  }, [tags, selectedTopTag]);

  const keywordRef = useRef<HTMLInputElement>(null);

  useKeyPress("Enter", () => {
    console.log("按下回车键");
    const keyword = keywordRef.current?.value;
    if (keyword) {
      updateChallengeList({
        search: keyword,
      });
    }
    console.log(keyword);
  });

  useEffect(() => {
    updateChallengeList({
      difficulties: selectedDifficulties as Difficulty[],
      status: selectedStatus as ChallengeStatus[],
      tags: selectedTags.reduce((prev, next) => prev.concat(next), []),
    });
  }, [selectedDifficulties, selectedStatus, selectedTags, updateChallengeList]);

  const COLUMNS = [
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 64,
      align: "center" as AlignType,
      render: (status: ChallengeStatus) => {
        if (status === "done") {
          return <CorrectFilled />;
        }
        if (status === "doing") {
          return <ErrorFilled />;
        }
        return null;
      },
    },
    {
      title: "题目",
      dataIndex: "title",
      key: "title",
      render: (title: string, record: ChallengeBase) => {
        const redirect = encodeURIComponent(location.pathname);
        return (
          <Link
            className={styles.link}
            to={`/problemset/${catalog}/code/${record.challenge_id}?redirect=${redirect}`}
          >
            {title}
          </Link>
        );
      },
    },
    {
      title: "通过率",
      dataIndex: "success_ratio",
      key: "success_ratio",
      width: 80,
      align: "center" as AlignType,
    },
    {
      title: "难度",
      dataIndex: "difficulty",
      key: "difficulty",
      width: 64,
      align: "center" as AlignType,
      render: (difficulty: Difficulty) => {
        if (difficulty === "Easy") {
          return <span style={{ color: "#40BF9C" }}>简单</span>;
        }
        if (difficulty === "Medium") {
          return <span style={{ color: "#FFB904" }}>中等</span>;
        }
        if (difficulty === "Hard") {
          return <span style={{ color: "#EB445A" }}>困难</span>;
        }
        return null;
      },
    },
  ];

  const navigate = useNavigate();
  const renderRecommendCourses = () => {
    return (
      <div className={styles.learnPlans}>
        <h4 className={styles.header}>推荐</h4>
        <div className={styles.body}>
          <img
            className={styles.image}
            src="https://python2fun.oss-cn-beijing.aliyuncs.com/probleamset/9b4ecc7a5f92616ce306c8000.png"
            alt=""
            onClick={() =>
              window.open(
                "https://edu.py2fun.com/learn#/course/015/catalog",
                "_blank"
              )
            }
          />
          <img
            className={styles.image}
            src="https://python2fun.oss-cn-beijing.aliyuncs.com/probleamset/9b4ecc7a5f92616ce306c8001.png"
            alt=""
            onClick={() =>
              window.open(
                "https://edu.py2fun.com/learn#/course/019/catalog",
                "_blank"
              )
            }
          />
          <img
            className={styles.image}
            src="https://python2fun.oss-cn-beijing.aliyuncs.com/probleamset/9b4ecc7a5f92616ce306c8002.png"
            alt=""
            onClick={() =>
              window.open(
                "https://edu.py2fun.com/learn#/course/018/catalog",
                "_blank"
              )
            }
          />
        </div>
      </div>
    );
  };

  const renderSearchBox = () => {
    return (
      <div className={styles.searchBox}>
        <input className={styles.input} type="text" ref={keywordRef} />
        <div className={styles.searchIcon} />
      </div>
    );
  };

  const handleClearTopTag = () => {
    setSelectedTopTag(null);
    setSelectedTags([]);
    if (keywordRef.current) {
      keywordRef.current.value = "";
    }
  };

  const handleTopTagClick = (tag: ChallengeTag) => {
    setSelectedTopTag(tag);
    setSelectedTags([[tag.id.toString()]]);
    if (keywordRef.current) {
      keywordRef.current.value = "";
    }
  };

  const renderCategoryTags = () => {
    return (
      <div className={styles.catalogs}>
        <Button
          className={styles.item}
          iconName="apps"
          width={79}
          onClick={handleClearTopTag}
        >
          全部
        </Button>
        {tags.map((tag) => (
          <Button
            key={tag.id}
            className={styles.item}
            width={79}
            onClick={() => handleTopTagClick(tag)}
            active={selectedTopTag?.id === tag.id}
          >
            {tag.name}
          </Button>
        ))}
      </div>
    );
  };

  const renderChallengeList = () => {
    return (
      <div className={styles.challengeList}>
        <Table
          rowKey="challenge_id"
          columns={COLUMNS}
          data={challengeList.results || []}
          emptyText={
            fetching ? (
              <div style={{ textAlign: "center" }}>加载中...</div>
            ) : (
              <div style={{ textAlign: "center" }}>暂无题目</div>
            )
          }
          footer={paginationRenderer}
        />
        {fetching && (
          <div className={styles.loading}>
            <Loading />
          </div>
        )}
      </div>
    );
  };

  const paginationRenderer = () => {
    return (
      <Pagination
        showTotal={(total) => `共${total}题`}
        total={challengeList.count}
        pageSize={40}
        current={Number(page)}
        showSizeChanger
        onChange={(page) => {
          navigate(`/problemset/${catalog}/${page}`);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    );
  };

  return (
    <>
      <Navigator activeKey="problemset" />
      <div className={styles.container}>
        <div className={styles.left}>
          <h4>英雄榜</h4>
          <RankList title="今日十佳" type="day" />
          <RankList title="七日十佳" type="week" />
          <RankList title="历史十佳" type="all" />
        </div>
        <main>
          {renderRecommendCourses()}
          {renderSearchBox()}
          {renderCategoryTags()}
          {renderChallengeList()}
        </main>
        <div className={styles.side}>
          <ChallengeCalendar className={styles.card} />
          <div className={styles.joinGroup} onClick={() => setShowQrcode(true)}>
            加入挑战社群，刷题不孤单
          </div>
          <div
            className={classes(styles.card, styles.currentProgress)}
            style={{ height: 194 }}
          >
            <div className={styles.title}>当前进度</div>
            <div className={styles.body}>
              <div className={styles.progress}>
                <Circle
                  className={styles.circle}
                  strokeWidth={4}
                  strokeColor="#575bfd"
                  percent={
                    (progress.finish_stat.All / progress.problem_stat.All) * 100
                  }
                />
                <div className={styles.title}>全部</div>
                <div className={styles.finished}>
                  {progress.finish_stat.All}
                </div>
                <div className={styles.divider} />
                <div className={styles.total}>{progress.problem_stat.All}</div>
              </div>
              <div className={styles.difficultyStats}>
                <DifficultyStats
                  label="简单"
                  finished={progress.finish_stat.Easy}
                  total={progress.problem_stat.Easy}
                  color="#4EAC9B"
                />
                <DifficultyStats
                  label="中等"
                  finished={progress.finish_stat.Medium}
                  total={progress.problem_stat.Medium}
                  color="#F4BB40"
                />
                <DifficultyStats
                  label="困难"
                  finished={progress.finish_stat.Hard}
                  total={progress.problem_stat.Hard}
                  color="#EB445A"
                />
              </div>
            </div>
          </div>
          <CardFilter
            title="状态"
            itemWidth={79}
            items={[
              { label: "已解答", value: "done" },
              { label: "尝试过", value: "doing" },
              { label: "未开始", value: "undo" },
            ]}
            values={selectedStatus as string[]}
            onChange={(value) => setSelectedStatus(value as ChallengeStatus[])}
            style={{ height: 102, marginTop: 12 }}
          />
          <CardFilter
            title="难度"
            itemWidth={79}
            items={[
              { label: "简单", value: "Easy" },
              { label: "中等", value: "Medium" },
              { label: "困难", value: "Hard" },
            ]}
            values={selectedDifficulties}
            onChange={(value) => setSelectedDifficulties(value as Difficulty[])}
            style={{ height: 102, marginTop: 12 }}
          />
          <DynamicTagsFilter
            values={selectedTags}
            onChange={(values) => setSelectedTags(values)}
            tags={sideTags || []}
          />
        </div>
      </div>
      <Modal
        className={styles.modal}
        visible={showQrcode}
        onClose={() => setShowQrcode(false)}
      >
        <img className={styles.qrcode} src={wechatGroupQrcode} alt="" />
      </Modal>
    </>
  );
};

export default Challenges;
